import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  IconChevronLeft,
  IconChevronRight,
  IconEdit,
  IconTrash,
  IconUserPlus,
  IconUsers,
  IconX,
  IconSearch,
  IconShield,
  IconDotsVertical,
  IconMail,
  IconUserOff,
  IconUserCheck,
} from '@tabler/icons-react';
import deleteIcon from '../../../assets/images/delete.png';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import loadingGif from '../../../assets/images/loader.gif';
import NPILookup from '../../../components/NPILookUp/NPILookUp';
import './UserManagement.css';

// Validation schema for the user form
const UserSchema = Yup.object().shape({
  firstName: Yup.string().trim().required('First name is required'),
  lastName: Yup.string().trim().required('Last name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  role: Yup.string().required('Role is required'),
  orgNpiNumber: Yup.string()
    .when('role', {
      is: (val) => val === 'PROVIDER',
      then: (schema) =>
        schema.required('NPI number is required for providers.'),
      otherwise: (schema) => schema.nullable().optional(),
    })
    .matches(
      /^\d{10}$/,
      'NPI number must be exactly 10 digits and contain only numbers',
    )
    .nullable()
    .optional(),
  selectedNpi: Yup.object()
    .nullable()
    .when('orgNpiNumber', {
      is: (val) => !!val, // If there's a value in orgNpiNumber
      then: (schema) =>
        schema.required('Please select a valid NPI number from suggestions'),
      otherwise: (schema) => schema.nullable().optional(),
    }),
});

// Initial form values
const INITIAL_FORM_VALUES = {
  firstName: '',
  lastName: '',
  email: '',
  role: '',
  orgNpiNumber: '',
  selectedNpi: '',
  hasAdminAccess: false,
};

// Helper function to get user initials
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

function UserManagement({
  users,
  totalUsers,
  totalPaginatedUsers,
  limit,
  roles,
  searchTerm,
  setSearchTerm,
  editingUser,
  setEditingUser,
  currentPage,
  totalPages,
  loadingStates,
  modalStates,
  statusChangeUser,
  loggedInUserAttributes,
  slotsInfo,
  handlePageChange,
  handleLimitChange,
  handleModalToggle,
  handleClearSearch,
  handleCreateUser,
  handleEditClick,
  handleUpdateUser,
  handleDeleteUser,
  handleStatusChange,
  setDeleteUserId,
  setStatusChangeUser,
  handleUpgradePlan,
  subscriptionDetails,
  isSubForceAllowed,
}) {
  // State for action dropdowns
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [roleFilter, setRoleFilter] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const actionButtonRefs = useRef({});
  const dropdownRef = useRef(null);

  // Calculate pagination info
  const startItem = users.length > 0 ? (currentPage - 1) * limit + 1 : 0;
  const endItem = users.length > 0 ? startItem + users.length - 1 : 0;

  // Calculate user counts for summary cards
  const adminCount = users.filter(u => u.has_admin_access).length;
  const regularUserCount = users.filter(u => !u.has_admin_access).length;

  // Filter users by search term and role
  const filteredUsers = users.filter(user => {
    // Filter by search term (name or email)
    const searchMatch = !searchTerm || 
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filter by role
    const roleMatch = !roleFilter ||
      (roleFilter === 'admin' && user.has_admin_access) ||
      (roleFilter === 'user' && !user.has_admin_access);
    
    return searchMatch && roleMatch;
  });

  // Calculate dropdown position when opening
  const handleActionMenuClick = (index, event) => {
    if (openActionMenu === index) {
      setOpenActionMenu(null);
    } else {
      const buttonRect = event.currentTarget.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const dropdownWidth = 160;
      const dropdownHeight = 150; // Approximate height
      
      // Calculate left position (prefer left of button, fall back to right)
      let left = buttonRect.right - dropdownWidth;
      if (left < 10) {
        // Not enough space on left, position to the right
        left = buttonRect.left;
      }
      
      // Calculate top position (prefer below button, fall back to above)
      let top = buttonRect.bottom + 4;
      if (top + dropdownHeight > viewportHeight - 10) {
        // Not enough space below, position above
        top = buttonRect.top - dropdownHeight - 4;
      }
      
      setDropdownPosition({
        top: Math.max(10, top),
        left: Math.max(10, Math.min(left, viewportWidth - dropdownWidth - 10)),
      });
      setOpenActionMenu(index);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside the dropdown and trigger button
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !event.target.closest('.actions-trigger')
      ) {
        setOpenActionMenu(null);
      }
    };

    if (openActionMenu !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openActionMenu]);

  // Close dropdown on scroll or resize
  useEffect(() => {
    const handleScrollOrResize = () => {
      if (openActionMenu !== null) {
        setOpenActionMenu(null);
      }
    };

    if (openActionMenu !== null) {
      window.addEventListener('scroll', handleScrollOrResize, true);
      window.addEventListener('resize', handleScrollOrResize);
    }

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [openActionMenu]);

  // Separate render function for status modal with Tailwind styling
  const renderStatusModal = () => {
    return (
      <div 
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000]" 
        onClick={() => handleModalToggle('statusModal', false)}
      >
        <div 
          className="bg-white rounded-xl w-full max-w-md mx-4 shadow-2xl" 
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-start justify-between p-6 border-b border-gray-200">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                {statusChangeUser?.enabled ? 'Deactivate' : 'Activate'} User
              </h3>
            </div>
            <button 
              onClick={() => handleModalToggle('statusModal', false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <IconX size={20} />
            </button>
          </div>
          
          {/* Body */}
          <div className="p-6">
            <p className="text-gray-600 text-center">
              Are you sure you want to{' '}
              {statusChangeUser?.enabled ? 'deactivate' : 'activate'} the user{' '}
              <span className="font-semibold text-gray-900">{statusChangeUser?.name}</span>?
            </p>
          </div>
          
          {/* Footer */}
          <div className="flex justify-end gap-3 px-6 pb-6">
            <button
              type="button"
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loadingStates.statusChange}
              onClick={() => handleModalToggle('statusModal', false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className={`px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                statusChangeUser?.enabled 
                  ? 'bg-blue-600 text-white hover:bg-blue-700' 
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
              onClick={handleStatusChange}
              disabled={loadingStates.statusChange}
            >
              {loadingStates.statusChange
                ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    {statusChangeUser?.enabled ? 'Deactivating...' : 'Activating...'}
                  </>
                )
                : statusChangeUser?.enabled
                  ? 'Deactivate'
                  : 'Activate'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Separate render function for delete modal with Tailwind styling
  const renderDeleteModal = () => {
    return (
      <div 
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000]" 
        onClick={() => handleModalToggle('deleteModal', false)}
      >
        <div 
          className="bg-white rounded-xl w-full max-w-md mx-4 shadow-2xl" 
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-start justify-between p-6 border-b border-gray-200">
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                Delete User
              </h3>
            </div>
            <button 
              onClick={() => handleModalToggle('deleteModal', false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <IconX size={20} />
            </button>
          </div>
          
          {/* Body */}
          <div className="p-6">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <IconTrash size={28} className="text-red-600" />
              </div>
            </div>
            <h4 className="text-lg font-semibold text-gray-900 text-center mb-2">
              Are you sure?
            </h4>
            <p className="text-gray-600 text-center">
              Once you delete the user, this action cannot be undone.
            </p>
          </div>
          
          {/* Footer */}
          <div className="flex justify-end gap-3 px-6 pb-6">
            <button
              type="button"
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loadingStates.delete}
              onClick={() => handleModalToggle('deleteModal', false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleDeleteUser}
              disabled={loadingStates.delete}
            >
              {loadingStates.delete ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderModal = (type) => {
    const { baseUsers, currentUsers } = slotsInfo;

    const modalConfig = {
      userModal: {
        title: editingUser ? 'Edit User' : 'Add User',
        subtitle: editingUser 
          ? 'Update team member information'
          : 'Add a new team member to your organization',
        content: (
          <Formik
            initialValues={editingUser || INITIAL_FORM_VALUES}
            validationSchema={UserSchema}
            onSubmit={editingUser ? handleUpdateUser : handleCreateUser}
            enableReinitialize
          >
            {({
              errors,
              touched,
              isSubmitting,
              values,
              dirty,
              setFieldValue,
              setFieldTouched,
              validateField,
            }) => (
              <Form>
                <div className="modern-modal-body">
                  {/* First Name and Last Name Row */}
                  <div className="form-row-2col">
                    <div className="form-field-modern">
                      <label htmlFor="firstName">
                        First Name<span className="required">*</span>
                      </label>
                      <Field
                        name="firstName"
                        id="firstName"
                        type="text"
                        className={`input-modern ${
                          errors.firstName && touched.firstName ? 'is-invalid' : ''
                        }`}
                        placeholder="John"
                        disabled={isSubmitting || loadingStates[editingUser ? 'edit' : 'create']}
                      />
                      {errors.firstName && touched.firstName && (
                        <span className="field-error-modern">{errors.firstName}</span>
                      )}
                    </div>

                    <div className="form-field-modern">
                      <label htmlFor="lastName">
                        Last Name<span className="required">*</span>
                      </label>
                      <Field
                        name="lastName"
                        id="lastName"
                        type="text"
                        className={`input-modern ${
                          errors.lastName && touched.lastName ? 'is-invalid' : ''
                        }`}
                        placeholder="Doe"
                        disabled={isSubmitting || loadingStates[editingUser ? 'edit' : 'create']}
                      />
                      {errors.lastName && touched.lastName && (
                        <span className="field-error-modern">{errors.lastName}</span>
                      )}
                    </div>
                  </div>

                  {/* Email Field */}
                  <div className="form-field-modern">
                    <label htmlFor="email">
                      Email<span className="required">*</span>
                    </label>
                    <div className="input-with-icon-modern">
                      <IconMail className="icon-left" size={18} />
                      <Field
                        name="email"
                        id="email"
                        type="email"
                        className={`${
                          errors.email && touched.email ? 'is-invalid' : ''
                        }`}
                        placeholder="john.doe@example.com"
                        disabled={
                          (editingUser && true) ||
                          isSubmitting ||
                          loadingStates[editingUser ? 'edit' : 'create']
                        }
                      />
                    </div>
                    {errors.email && touched.email && (
                      <span className="field-error-modern">{errors.email}</span>
                    )}
                  </div>

                  {/* Title Field */}
                  <div className="form-field-modern" style={{ marginTop: '20px' }}>
                    <label htmlFor="role">Title</label>
                    <div className="input-with-icon-modern">
                      <IconShield className="icon-left" size={18} />
                      <Field
                        as="select"
                        name="role"
                        id="role"
                        className={`${
                          errors.role && touched.role ? 'is-invalid' : ''
                        }`}
                        onChange={(e) => {
                          setFieldTouched('role');
                          setFieldValue('role', e.target.value).then(() => {
                            validateField('role');
                          });
                          setFieldValue('orgNpiNumber', '');
                          setFieldValue('selectedNpi', { npi: '' });
                        }}
                        disabled={
                          isSubmitting ||
                          loadingStates[editingUser ? 'edit' : 'create']
                        }
                      >
                        <option value="">Select Role</option>
                        {roles.map((role) => (
                          <option key={role.value} value={role.value}>
                            {role.name}
                          </option>
                        ))}
                      </Field>
                    </div>
                    {errors.role && touched.role && (
                      <span className="field-error-modern">{errors.role}</span>
                    )}
                  </div>

                  {/* NPILookup Component */}
                  {values.role === 'PROVIDER' && (
                    <div style={{ marginTop: '20px' }}>
                      <NPILookup
                        name="orgNpiNumber"
                        label="NPI Number"
                        disabled={isSubmitting}
                      >
                        {touched.selectedNpi && errors.selectedNpi && (
                          <div className="field-error-modern">
                            {errors.selectedNpi}
                          </div>
                        )}
                      </NPILookup>
                    </div>
                  )}

                  {/* Grant Administrative Access */}
                  <div className="admin-access-card">
                    <Field
                      type="checkbox"
                      name="hasAdminAccess"
                      id="hasAdminAccess"
                      disabled={
                        isSubmitting ||
                        loadingStates[editingUser ? 'edit' : 'create']
                      }
                    />
                    <label htmlFor="hasAdminAccess" className="admin-access-content">
                      <div className="admin-access-title">
                        Grant Administrative Access
                      </div>
                      <div className="admin-access-description">
                        Admins can manage organization settings, billing, and users.
                      </div>
                    </label>
                  </div>
                </div>

                <div className="modern-modal-footer">
                  <button
                    type="button"
                    className="btn-modern btn-modern-cancel"
                    disabled={
                      isSubmitting ||
                      loadingStates[editingUser ? 'edit' : 'create']
                    }
                    onClick={() => {
                      setEditingUser(null);
                      handleModalToggle('userModal', false);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-modern btn-modern-primary"
                    disabled={
                      isSubmitting ||
                      loadingStates[editingUser ? 'edit' : 'create'] ||
                      !dirty
                    }
                  >
                    {isSubmitting || loadingStates[editingUser ? 'edit' : 'create']
                      ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          {editingUser ? 'Updating...' : 'Adding...'}
                        </>
                      )
                      : editingUser
                        ? 'Update'
                        : 'Add User'}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        ),
      },
    };

    const { title, content } = modalConfig[type];

    return (
      <div className="modern-modal" onClick={() => handleModalToggle(type, false)}>
        <div 
          className="modern-modal-content" 
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modern-modal-header">
            <div>
              <h3 className="modern-modal-title">{title}</h3>
              {type === 'userModal' && (
                <p className="modern-modal-subtitle">
                  {editingUser ? 'Update user information and permissions' : 'Add a new team member to your organization'}
                </p>
              )}
            </div>
            <button 
              className="modern-modal-close"
              onClick={() => handleModalToggle(type, false)}
            >
              ×
            </button>
          </div>
          {content}
        </div>
      </div>
    );
  };

  return (
    <div className="user-management-container">
      {/* User Summary Section */}
      <div className="user-summary-section">
        <div className="user-summary-header">
          <h2>User Summary</h2>
          <p>Monitor your team capacity</p>
        </div>

        <div className="user-summary-cards">
          {/* Total Users Card with Breakdown */}
          <div className="user-summary-card">
            <div className="summary-icon">
              <IconUsers size={24} />
            </div>
            <div className="summary-content">
              <p className="summary-label">Total Users</p>
              <h3 className="summary-value">{totalUsers}</h3>
              {/* Note: Currently enforcing total seat count only (provider/support distinction temporarily disabled) */}
              <div className="summary-breakdown">
                <div className="breakdown-item">
                  <span>Total Seats</span>
                  <span className="breakdown-value">
                    {slotsInfo?.currentUsers} of {slotsInfo?.baseUsers}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Admins Card */}
          <div className="user-summary-card">
            <div className="summary-icon">
              <IconShield size={24} />
            </div>
            <div className="summary-content">
              <p className="summary-label">Admins</p>
              <h3 className="summary-value">{adminCount}</h3>
            </div>
          </div>

          {/* Users Card */}
          <div className="user-summary-card">
            <div className="summary-icon">
              <IconUsers size={24} />
            </div>
            <div className="summary-content">
              <p className="summary-label">Users</p>
              <h3 className="summary-value">{regularUserCount}</h3>
            </div>
          </div>
        </div>
      </div>

      {/* User Management Section */}
      <div className="user-management-section">
        <div className="user-management-header">
          <h3>User Management</h3>
          <p>Add, remove, or update team members and manage administrative access</p>
        </div>

        {/* Controls Row */}
        <div className="user-controls">
          <div className="user-controls-left">
            {/* Search Input */}
            <div className="user-search-input">
              <IconSearch className="search-icon" size={18} />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  className="clear-search-btn"
                  onClick={handleClearSearch}
                  type="button"
                >
                  <IconX size={18} />
                </button>
              )}
            </div>

            {/* Role Filter */}
            <div className="user-role-filter">
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
              >
                <option value="">All Roles</option>
                <option value="admin">Admin</option>
                <option value="user">User</option>
              </select>
            </div>
          </div>

          {/* Add User Button */}
          <OverlayTrigger
            placement="bottom"
            overlay={
              !isSubForceAllowed && slotsInfo?.currentUsers >= slotsInfo?.baseUsers ? (
                <Tooltip id="tooltip">
                  <strong>
                    {slotsInfo?.isFreeTrialOrg || subscriptionDetails?.status === 'trialing'
                      ? 'User limit reached for your trial. Upgrade to add more team members.'
                      : 'Plan upgrade required to add more users.'}
                  </strong>
                </Tooltip>
              ) : (
                <></>
              )
            }
          >
            <div>
              <button
                type="button"
                className="btn-add-user"
                onClick={() => handleModalToggle('userModal', true)}
                disabled={!isSubForceAllowed && slotsInfo?.currentUsers >= slotsInfo?.baseUsers}
              >
                <IconUserPlus size={18} />
                Add User
              </button>
            </div>
          </OverlayTrigger>
        </div>

        {/* Users Table */}
        <div className="users-table-wrapper">
          {loadingStates.search ? (
            <div className="loading-state">
              <img
                width={140}
                src={loadingGif}
                alt="Searching users..."
                className="loader-gif"
              />
            </div>
          ) : filteredUsers.length > 0 ? (
            <table className="users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Title</th>
                  <th>User Role</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user, index) => {
                  const userInitials = getInitials(user.name);
                  const isCurrentUser = user.user_name === loggedInUserAttributes.sub;
                  const isOrgOwner = user.user_type_key === 'ORG_OWNER' ||
                    loggedInUserAttributes['custom:org_owner_sub'] === user.user_name;
                  const canEdit = !isOrgOwner && !isCurrentUser;

                  return (
                    <tr key={`user-${index}`}>
                      <td data-label="Name">
                        <div className="user-name-cell">
                          <div className="user-avatar">{userInitials}</div>
                          <span className="user-name">{user.name}</span>
                        </div>
                      </td>
                      <td data-label="Email">{user.email}</td>
                      <td data-label="Title">
                        <span className="role-badge">
                          <span className="role-text">{user.user_type}</span>
                        </span>
                      </td>
                      <td data-label="User Role">
                        <span className={`user-role-text ${user.has_admin_access ? 'admin' : ''}`}>
                          {user.has_admin_access ? 'Admin' : 'User'}
                        </span>
                      </td>
                      <td data-label="Status">
                        <span className={`status-badge ${user.enabled ? 'active' : 'inactive'}`}>
                          {user.enabled ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td data-label="Actions">
                        {canEdit ? (
                          <div className="actions-menu">
                            <button
                              className="actions-trigger"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleActionMenuClick(index, e);
                              }}
                              type="button"
                            >
                              <IconDotsVertical size={20} />
                            </button>
                            {openActionMenu === index &&
                              createPortal(
                                <div
                                  ref={dropdownRef}
                                  className="actions-dropdown"
                                  style={{
                                    position: 'fixed',
                                    top: `${dropdownPosition.top}px`,
                                    left: `${dropdownPosition.left}px`,
                                    zIndex: 99999,
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <button
                                    className="actions-dropdown-item"
                                    onClick={() => {
                                      handleEditClick(user);
                                      setOpenActionMenu(null);
                                    }}
                                    type="button"
                                    disabled={!user.enabled}
                                  >
                                    <IconEdit size={16} />
                                    Edit
                                  </button>
                                  <button
                                    className={`actions-dropdown-item ${user.enabled ? 'warning' : 'success'}`}
                                    onClick={() => {
                                      setStatusChangeUser(user);
                                      handleModalToggle('statusModal', true);
                                      setOpenActionMenu(null);
                                    }}
                                    type="button"
                                  >
                                    {user.enabled ? (
                                      <>
                                        <IconUserOff size={16} />
                                        Deactivate
                                      </>
                                    ) : (
                                      <>
                                        <IconUserCheck size={16} />
                                        Activate
                                      </>
                                    )}
                                  </button>
                                  <button
                                    className="actions-dropdown-item danger"
                                    onClick={() => {
                                      setDeleteUserId(user.user_name);
                                      handleModalToggle('deleteModal', true);
                                      setOpenActionMenu(null);
                                    }}
                                    type="button"
                                  >
                                    <IconTrash size={16} />
                                    Remove
                                  </button>
                                </div>,
                                document.body
                              )}
                          </div>
                        ) : (
                          <span style={{ color: '#9ca3af' }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="empty-state">
              <IconUsers size={48} />
              <h4>No users found</h4>
              <p>Try adjusting your search or filters</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {filteredUsers.length > 0 && (
          <div className="user-pagination">
            <div className="pagination-info">
              Showing {startItem}-{endItem} of {totalPaginatedUsers} users
            </div>
            <div className="pagination-controls">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loadingStates.search}
                type="button"
              >
                <IconChevronLeft size={20} />
              </button>
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={
                  totalPages === 0 ||
                  currentPage === totalPages ||
                  loadingStates.search
                }
                type="button"
              >
                <IconChevronRight size={20} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {modalStates.userModal && renderModal('userModal')}
      {modalStates.deleteModal && renderDeleteModal()}
      {modalStates.statusModal && renderStatusModal()}
      {modalStates.userModal && <div className="modal-backdrop show" />}
    </div>
  );
}

export default UserManagement;
